// Admin-Panel › Limits: alle PlatformLimits mit Einheiten; PATCH nur geänderter Felder in Basiseinheit.
import {
  DEFAULT_LIMITS,
  SCREENSHARE_PRESET_SPECS,
  SCREENSHARE_PRESETS,
  type PlatformLimitKey,
  type PlatformLimits,
  type ScreensharePreset,
} from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { adminApi } from '../api';
import { formatBytes } from '../format';
import { toast } from '../toast';
import { Button, ErrorBox, Loading } from '../ui';
import {
  bestByteUnit,
  BYTE_UNITS,
  diffLimits,
  displayText,
  fromDisplay,
  LIMIT_FIELDS,
  LIMIT_GROUPS,
  LIMIT_KEYS,
  zeroMeansUnlimited,
  type ByteUnit,
  type DisplayUnit,
} from './limits-units';
import { AdminError, AdminSection } from './parts';
import { ADMIN_LIMITS_KEY } from './realtime';

type Texts = Partial<Record<PlatformLimitKey, string>>;
type Units = Partial<Record<PlatformLimitKey, ByteUnit>>;
type Flags = { registrationOpen?: boolean; screenshareMaxPreset?: ScreensharePreset };

function presetLabel(p: ScreensharePreset): string {
  const s = SCREENSHARE_PRESET_SPECS[p];
  return `${p} – ${s.width}×${s.height}, ${s.fps} fps`;
}

function LimitsEditor(props: { current: PlatformLimits }) {
  const current = props.current;
  const qc = useQueryClient();
  const formId = useId();
  const [texts, setTexts] = useState<Texts>({});
  const [units, setUnits] = useState<Units>({});
  const [flags, setFlags] = useState<Flags>({});

  const unitOf = (key: PlatformLimitKey): DisplayUnit => {
    const meta = LIMIT_FIELDS[key];
    if (meta.kind === 'bytes') return units[key] ?? bestByteUnit(current[key] as number);
    if (meta.kind === 'number') return meta.unit;
    return 'count';
  };
  const textOf = (key: PlatformLimitKey) => texts[key] ?? displayText(current[key] as number, unitOf(key));
  const parsed = (key: PlatformLimitKey): number | null => {
    const t = texts[key];
    return t === undefined ? (current[key] as number) : fromDisplay(t, unitOf(key));
  };
  const errorOf = (key: PlatformLimitKey): string | null => {
    const meta = LIMIT_FIELDS[key];
    if (meta.kind !== 'bytes' && meta.kind !== 'number') return null;
    const v = parsed(key);
    if (v === null) return 'Bitte eine Zahl ≥ 0 eingeben.';
    if (v < meta.min) return 'Muss größer als 0 sein.';
    return null;
  };

  const next: Partial<PlatformLimits> = { ...flags };
  for (const key of Object.keys(texts) as PlatformLimitKey[]) {
    const v = parsed(key);
    if (v !== null) (next as Record<string, unknown>)[key] = v;
  }
  const changes = diffLimits(current, next);
  const changedKeys = Object.keys(changes) as PlatformLimitKey[];
  const errors = LIMIT_KEYS.map((k) => [k, errorOf(k)] as const).filter(([, e]) => e);
  const hasEdits = Object.keys(texts).length > 0 || Object.keys(flags).length > 0 || Object.keys(units).length > 0;

  const discard = () => {
    setTexts({});
    setUnits({});
    setFlags({});
  };
  const save = useMutation({
    mutationFn: (body: Partial<PlatformLimits>) => adminApi.updateLimits(body),
    onSuccess: (limits) => {
      qc.setQueryData(ADMIN_LIMITS_KEY, limits);
      discard();
      toast({ kind: 'success', title: 'Limits gespeichert' });
    },
  });

  const setText = (key: PlatformLimitKey, value: string) => setTexts((t) => ({ ...t, [key]: value }));
  const changeUnit = (key: PlatformLimitKey, unit: ByteUnit) => {
    const base = parsed(key);
    setUnits((u) => ({ ...u, [key]: unit }));
    if (texts[key] !== undefined && base !== null) setText(key, displayText(base, unit));
  };
  const resetToDefault = (key: PlatformLimitKey) => {
    const meta = LIMIT_FIELDS[key];
    if (meta.kind === 'boolean') setFlags((f) => ({ ...f, registrationOpen: DEFAULT_LIMITS.registrationOpen }));
    else if (meta.kind === 'preset') setFlags((f) => ({ ...f, screenshareMaxPreset: DEFAULT_LIMITS.screenshareMaxPreset }));
    else {
      const def = DEFAULT_LIMITS[key] as number;
      const unit = meta.kind === 'bytes' ? bestByteUnit(def) : meta.unit;
      if (meta.kind === 'bytes') setUnits((u) => ({ ...u, [key]: unit as ByteUnit }));
      setText(key, displayText(def, unit));
    }
  };
  const effective = (key: PlatformLimitKey): unknown => {
    if (key === 'registrationOpen') return flags.registrationOpen ?? current.registrationOpen;
    if (key === 'screenshareMaxPreset') return flags.screenshareMaxPreset ?? current.screenshareMaxPreset;
    return parsed(key);
  };

  const renderField = (key: PlatformLimitKey) => {
    const meta = LIMIT_FIELDS[key];
    const id = `${formId}-${key}`;
    const hintId = `${id}-hint`;
    const isDefault = effective(key) === DEFAULT_LIMITS[key];
    const changed = changedKeys.includes(key);
    const err = errorOf(key);
    let control;
    let defaultText: string;
    if (meta.kind === 'boolean') {
      control = (
        <label className="lfx-check">
          <input
            id={id}
            type="checkbox"
            checked={flags.registrationOpen ?? current.registrationOpen}
            aria-describedby={hintId}
            data-testid={`admin-limits-input-${key}`}
            onChange={(e) => setFlags((f) => ({ ...f, registrationOpen: e.target.checked }))}
          />
          {meta.label}
        </label>
      );
      defaultText = DEFAULT_LIMITS.registrationOpen ? 'offen' : 'geschlossen';
    } else if (meta.kind === 'preset') {
      control = (
        <select
          id={id}
          className="lfx-select"
          value={flags.screenshareMaxPreset ?? current.screenshareMaxPreset}
          aria-describedby={hintId}
          data-testid={`admin-limits-input-${key}`}
          onChange={(e) => setFlags((f) => ({ ...f, screenshareMaxPreset: e.target.value as ScreensharePreset }))}
        >
          {SCREENSHARE_PRESETS.map((p) => (
            <option key={p} value={p}>
              {presetLabel(p)}
            </option>
          ))}
        </select>
      );
      defaultText = presetLabel(DEFAULT_LIMITS.screenshareMaxPreset);
    } else {
      const def = DEFAULT_LIMITS[key] as number;
      defaultText = meta.kind === 'bytes' ? formatBytes(def) : `${displayText(def, meta.unit)} ${meta.unitLabel}`;
      control = (
        <div className="lfx-admin-limit__control">
          <input
            id={id}
            className="lfx-input lfx-admin-limit__input"
            inputMode="decimal"
            value={textOf(key)}
            aria-invalid={err ? true : undefined}
            aria-describedby={hintId}
            data-testid={`admin-limits-input-${key}`}
            onChange={(e) => setText(key, e.target.value)}
          />
          {meta.kind === 'bytes' ? (
            <select
              className="lfx-select lfx-admin-limit__unit"
              aria-label={`Einheit für ${meta.label}`}
              value={unitOf(key)}
              data-testid={`admin-limits-unit-${key}`}
              onChange={(e) => changeUnit(key, e.target.value as ByteUnit)}
            >
              {BYTE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          ) : (
            <span className="lfx-muted lfx-admin-limit__unitlabel">{meta.unitLabel}</span>
          )}
        </div>
      );
    }
    return (
      <div key={key} className={`lfx-admin-limit${changed ? ' lfx-admin-limit--changed' : ''}`} data-testid={`admin-limits-field-${key}`}>
        {meta.kind !== 'boolean' && (
          <label className="lfx-field__label" htmlFor={id}>
            {meta.label}
            {changed && <span className="lfx-admin-limit__dot" aria-label="geändert" title="geändert" />}
          </label>
        )}
        {control}
        <div className="lfx-field__hint" id={hintId}>
          Standard: {defaultText}
          {zeroMeansUnlimited(key) && ' · 0 = unbegrenzt'}
          {meta.hint && ` · ${meta.hint}`}
        </div>
        {err && (
          <div className="lfx-field__error" role="alert" data-testid={`admin-limits-error-${key}`}>
            {err}
          </div>
        )}
        <div>
          <button
            type="button"
            className="lfx-admin-textbtn"
            disabled={isDefault}
            data-testid={`admin-limits-reset-${key}`}
            onClick={() => resetToDefault(key)}
          >
            Auf Standard zurücksetzen
          </button>
        </div>
      </div>
    );
  };

  return (
    <form
      className="lfx-stack"
      data-testid="admin-limits-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (errors.length || changedKeys.length === 0) return;
        save.mutate(changes);
      }}
    >
      {LIMIT_GROUPS.map((g) => (
        <fieldset key={g.id} className="lfx-card lfx-admin-limits-group" data-testid={`admin-limits-group-${g.id}`}>
          <legend className="lfx-admin-limits-group__legend">{g.label}</legend>
          <div className="lfx-admin-limits-grid">{LIMIT_KEYS.filter((k) => LIMIT_FIELDS[k].group === g.id).map(renderField)}</div>
        </fieldset>
      ))}
      <ErrorBox error={save.error} testId="admin-limits-save-error" />
      <div className="lfx-row lfx-admin-sticky" role="group" aria-label="Änderungen">
        <span className="lfx-muted lfx-grow" data-testid="admin-limits-changed-count">
          {changedKeys.length === 0 ? 'Keine Änderungen' : `${changedKeys.length} Änderung${changedKeys.length === 1 ? '' : 'en'}`}
          {errors.length > 0 && ` · ${errors.length} ungültig`}
        </span>
        <Button testId="admin-limits-discard" disabled={!hasEdits || save.isPending} onClick={discard}>
          Änderungen verwerfen
        </Button>
        <Button type="submit" variant="primary" testId="admin-limits-save" disabled={changedKeys.length === 0 || errors.length > 0 || save.isPending}>
          Speichern
        </Button>
      </div>
    </form>
  );
}

export function LimitsPage() {
  const query = useQuery({ queryKey: ADMIN_LIMITS_KEY, queryFn: adminApi.limits });
  return (
    <AdminSection title="Plattform-Limits" testId="admin-limits">
      <p className="lfx-muted">Grenzen für alle Nutzer. Änderungen gelten sofort und werden an alle verbundenen Clients verteilt.</p>
      {query.isLoading && <Loading testId="admin-limits-loading" />}
      <AdminError error={query.error} testId="admin-limits-error" />
      {query.data && <LimitsEditor current={query.data} />}
    </AdminSection>
  );
}
